import { RivalsAppProps, RivalsPageProps } from "rivals/Context";
import Teams from "rivals/components/pages/Teams";
import {
  TEAM_TYPES,
  Props as TeamsPageProps
} from "rivals/components/pages/Teams/types";
import {
  getDefaultGroupingValue,
  mapSportParamToPrimarySport,
  mapTypeParamToTeamType
} from "rivals/components/pages/Teams/utils";
import {
  fetchConference as fetchConferenceAction,
  fetchConferences
} from "rivals/redux/conference/actions";
import {
  fetchHighschoolGroup as fetchHighschoolGroupAction,
  fetchHighschoolGroups
} from "rivals/redux/highschool/actions";
import { StateKeys } from "rivals/redux/reducer";
import { RESPONSE_CODE_TEMPORARY_REDIRECT } from "rivals/services/base";
import { PATHS } from "rivals/shared/constants";
import { replaceMultiple } from "rivals/shared/utils/strings";

interface Props extends RivalsPageProps, TeamsPageProps {}

const PATH_SEGMENTS_INDICES = {
  GROUP: 2,
  SPORT: 0,
  TYPE: 1
};

function TeamsPage(props: Props): JSX.Element {
  return (
    <main role="main">
      <Teams
        group={props.group}
        groupingType={props.groupingType}
        sport={props.sport}
        type={props.type}
      />
    </main>
  );
}

TeamsPage.getInitialProps = async ({
  isServer,
  query,
  res,
  store: { dispatch, getState }
}: RivalsAppProps): Promise<Props> => {
  const slug = query.slug;
  const { GROUP, SPORT, TYPE } = PATH_SEGMENTS_INDICES;
  const sport = mapSportParamToPrimarySport(slug[SPORT]);
  const type = mapTypeParamToTeamType(slug[TYPE]);
  const groupingType = getDefaultGroupingValue(type);
  const group = slug[GROUP] || "";

  if (group) {
    // When only a single group is selected
    if (type === TEAM_TYPES.COLLEGES) {
      // And when the team type is `colleges`, it means it's a college conference.
      // Gets that conference data
      await dispatch(
        fetchConferenceAction({
          abbreviation: group,
          grouped: false,
          includeColleges: true,
          sport,
          withSites: true
        })
      );
      // Gets the conferences dropdown options without each conference colleges
      // and only the ones that their colleges have sites.
      await dispatch(
        fetchConferences({
          grouped: false,
          sport,
          withSites: true
        })
      );
    } else {
      // But when the team type is `highschools`, it means it's a high school group
      // Get that group dadta
      await dispatch(
        fetchHighschoolGroupAction({
          region: group
        })
      );

      // Gets the highschool groups dropdown options
      await dispatch(fetchHighschoolGroups());
    }
  } else {
    // When a single group isn't selected
    if (type === TEAM_TYPES.COLLEGES) {
      // Gets all college conferences with the colleges that belong to each one
      // and only the ones which their colleges have sites.
      await dispatch(
        fetchConferences({
          collegeLetterGroups: true,
          grouped: false,
          includeColleges: true,
          sport,
          withSites: true
        })
      );
    } else {
      await dispatch(
        fetchHighschoolGroups({
          includeSites: true,
          letterGrouped: true
        })
      );
    }
  }

  const state = getState();
  if (group) {
    const conference = state[StateKeys.CONFERENCE].conference;
    const highschoolGroup = state[StateKeys.HIGHSCHOOL].highschoolGroup;
    if (
      (type === TEAM_TYPES.COLLEGES && !conference) ||
      (type === TEAM_TYPES.HIGH_SCHOOLS && !highschoolGroup)
    ) {
      const targetPath = replaceMultiple(PATHS.TEAMS, {
        /* eslint-disable @typescript-eslint/naming-convention */
        "[group]": "",
        "[sport]": sport,
        "[type]": type
        /* eslint-enable @typescript-eslint/naming-convention */
      });
      if (isServer && res) {
        res.writeHead(RESPONSE_CODE_TEMPORARY_REDIRECT, {
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          "Content-Type": "text/html; charset=utf-8",
          Location: targetPath
        });
        res.end();
      } else {
        window.location.pathname = targetPath;
        // avoids returning errors or props to render the page
        // it just waits with a promise that is never resolved or rejected until the user is redirected
        await new Promise(() => {});
      }
    }
  }

  return {
    group,
    groupingType,
    isServer,
    sport,
    type
  };
};

export default TeamsPage;
